import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Carry-over alert pill — shown when the backend silently auto-closes a
 * break/visit that the staff forgot to End before midnight.
 *
 *  • Polls `/api/staff/auto-closures` on mount (and after each break/visit
 *    start via the `refresh` event the parent fires).
 *  • One yellow card lists every pending closure with `kind • label • date`.
 *  • "Got it" → POSTs `/dismiss`, hides the card until the next auto-close.
 *
 * Pure additive UI — does not block any other action.
 */
export default function CarryOverAlert() {
  const [items, setItems] = useState([]);
  const [dismissing, setDismissing] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_URL}/api/staff/auto-closures`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const d = await r.json();
      setItems(Array.isArray(d.items) ? d.items : []);
    } catch (_) { /* silent */ }
  }, []);

  useEffect(() => {
    fetchItems();
    const onRefresh = () => fetchItems();
    window.addEventListener('carryover:refresh', onRefresh);
    const i = setInterval(fetchItems, 60_000);
    return () => {
      window.removeEventListener('carryover:refresh', onRefresh);
      clearInterval(i);
    };
  }, [fetchItems]);

  const dismiss = async () => {
    setDismissing(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/staff/auto-closures/dismiss`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems([]);
    } finally { setDismissing(false); }
  };

  if (!items.length) return null;

  return (
    <div
      className="mb-3 rounded-xl border border-amber-300 bg-amber-50 p-3 flex items-start gap-3 shadow-sm"
      data-testid="carryover-alert"
    >
      <div className="p-1.5 bg-amber-200 rounded-full text-amber-800 flex-shrink-0 mt-0.5">
        <AlertTriangle size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-900">
          {items.length === 1 ? 'We closed a stale item from earlier' : `We closed ${items.length} stale items from earlier`}
        </p>
        <p className="text-xs text-amber-800 mt-0.5">
          Looks like you forgot to End a {items[0].kind} before signing off. We auto-closed it so you can continue today.
        </p>
        <ul className="mt-2 space-y-1 text-xs text-amber-900">
          {items.slice(0, 4).map((it) => (
            <li key={`${it.kind}-${it.id}`} className="flex items-baseline gap-1.5" data-testid={`carryover-item-${it.kind}`}>
              <span className="px-1.5 py-0.5 rounded bg-amber-200 font-mono text-[10px] uppercase">{it.kind}</span>
              <span className="truncate">{it.label}</span>
              {it.date && <span className="text-amber-700">· {it.date}</span>}
            </li>
          ))}
          {items.length > 4 && (
            <li className="text-amber-700">…and {items.length - 4} more</li>
          )}
        </ul>
        <button
          onClick={dismiss}
          disabled={dismissing}
          className="mt-2 px-3 py-1 text-xs bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 text-white rounded-md"
          data-testid="carryover-dismiss-btn"
        >
          {dismissing ? 'Dismissing…' : 'Got it'}
        </button>
      </div>
      <button
        onClick={dismiss}
        disabled={dismissing}
        className="text-amber-600 hover:text-amber-900 flex-shrink-0"
        aria-label="Dismiss carry-over"
        data-testid="carryover-close-btn"
      >
        <X size={14} />
      </button>
    </div>
  );
}
